'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Room {
  id: number;
  name: string;
  description: string;
  guests: number;
  beds: string;
  size: string;
  price: number;
  currency: string;
  amenities: string[];
  image: string;
  imageAlt: string;
  available: boolean;
}

interface RoomsListProps {
  rooms: Room[];
  checkIn?: string;
  checkOut?: string;
  onSelectRoom: (room: Room) => void;
}

export default function RoomsList({ rooms, checkIn, checkOut, onSelectRoom }: RoomsListProps) {
  const nights = checkIn && checkOut
    ? Math.max(1, Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24)))
    : 1;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Rooms</h2>
      <div className="space-y-4">
        {rooms.map((room) => (
          <div
            key={room.id}
            className={`border rounded-2xl overflow-hidden transition-all duration-200 ${room.available ? 'border-gray-200 hover:border-gray-300 hover:shadow-md' : 'border-gray-100 opacity-60'}`}
          >
            <div className="flex flex-col sm:flex-row">
              {/* Room image */}
              <div className="relative w-full sm:w-56 h-44 sm:h-auto flex-shrink-0">
                <AppImage
                  src={room.image}
                  alt={room.imageAlt}
                  fill
                  className="object-cover"
                />
                {!room.available && (
                  <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                    <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Unavailable</span>
                  </div>
                )}
              </div>

              {/* Room details */}
              <div className="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{room.name}</h3>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xl font-bold text-gray-900">
                        {room.currency} {room.price.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">per night</div>
                      {nights > 1 && (
                        <div className="text-sm text-gray-600 mt-0.5">
                          {room.currency} {(room.price * nights).toLocaleString()} total
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 leading-relaxed">{room.description}</p>

                  {/* Room specs */}
                  <div className="flex flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {room.guests} guests
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      {room.beds}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      {room.size}
                    </div>
                  </div>

                  {/* Room amenities */}
                  <div className="flex flex-wrap gap-1.5">
                    {room.amenities.slice(0, 4).map((a, i) => (
                      <span key={i} className="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">{a}</span>
                    ))}
                    {room.amenities.length > 4 && (
                      <span className="text-xs text-gray-500 px-2.5 py-1">+{room.amenities.length - 4} more</span>
                    )}
                  </div>
                </div>

                {/* Select button */}
                <div className="mt-4">
                  <button
                    onClick={() => room.available && onSelectRoom(room)}
                    disabled={!room.available}
                    className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      room.available
                        ? 'bg-[#4088C6] hover:bg-[#3070a6] text-white shadow-sm hover:shadow-md'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {room.available ? 'Select Room' : 'Unavailable'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
